import { autoinject, bindable, observable } from 'aurelia-framework';
import { AsyncErrorHandler } from 'lib/ui';
import { Models } from 'models/core';
import { ConsumptionMaterialWashingService } from 'services/consumption-material-washing-service';
import { LocationCodeService } from 'services/location-code-service';

type Model = {
  ConsumptionMaterialWashingId: number | undefined;
  LocationCodeId: number | null | undefined;
  Comment: string | undefined;
  ConsumptionMaterialWashingAmount: number | undefined;
  Id?: number;
};

type data = { data: ServiceConsumptionMaterialWashingModel };

export type ServiceConsumptionMaterialWashingModel = {
  ServiceId: number;
  ConsumptionMaterialWashingId: number;
  LocationCodeId: number | null;
  Comment: string;
  ConsumptionMaterialWashingAmount: number;
  Id?: number;
};

@autoinject
export class ServiceConsumptionMaterialWashingCreateEdit {
  @bindable
  @observable
  model?: Model;
  targetModel: Model = {
    ConsumptionMaterialWashingId: 0,
    LocationCodeId: null,
    Comment: '',
    ConsumptionMaterialWashingAmount: 0,
  };
  modelChanged(model: Model) {
    this.targetModel = JSON.parse(JSON.stringify(model));

    this.form.ConsumptionMaterialWashingId = this.targetModel.ConsumptionMaterialWashingId;
    this.form.LocationCodeId = this.targetModel.LocationCodeId;
    this.form.Comment = this.targetModel.Comment;
    this.form.ConsumptionMaterialWashingAmount = this.targetModel.ConsumptionMaterialWashingAmount;
  }

  @bindable service!: Models.Service;

  @bindable onSave?: (data: data) => void | Promise<void>;
  @bindable onCanceled?: () => void;

  @bindable
  @observable
  visible = false;

  protected visibleChanged(newValue: boolean) {
    // When the form becomes visible in "create" mode (no existing model),
    // preselect the location code based on the service station's
    // default location code (if configured).
    if (newValue && !this.model) {
      this.applyDefaultLocationCode();
    }
  }

  private applyDefaultLocationCode() {
    const defaultLocationCodeId =
      this.service?.ServiceStation?.DefaultLocationCodeId;

    if (!defaultLocationCodeId) {
      return;
    }

    if (!this.form.LocationCodeId) {
      this.form.LocationCodeId = defaultLocationCodeId;
    }
  }

  protected form: Model = {
    ConsumptionMaterialWashingId: undefined,
    LocationCodeId: undefined,
    Comment: undefined,
    ConsumptionMaterialWashingAmount: undefined,
  };

  constructor(private consumptionMaterialWashingService: ConsumptionMaterialWashingService, private locationCodeService: LocationCodeService,) {}

  protected async getServiceConsumptionMaterialWashings() {
    return await this.consumptionMaterialWashingService.getAll();
  }

  private createModel() {
    const source = this.targetModel;
    const model: ServiceConsumptionMaterialWashingModel = {
      ServiceId: this.service.Id,
      ConsumptionMaterialWashingId: this.form.ConsumptionMaterialWashingId!,
      LocationCodeId: this.form.LocationCodeId || null,
      Comment: this.form.Comment!,
      ConsumptionMaterialWashingAmount: this.form.ConsumptionMaterialWashingAmount!,
    };

    if (source.Id) {
      model.Id = source.Id;
    }

    return model;
  }

  async save() {
    await this.onSave?.({
      data: this.createModel(),
    });
    this.resetForm();
  }

  resetForm() {
    this.form.ConsumptionMaterialWashingId = 0;
    this.form.LocationCodeId = null;
    this.form.Comment = undefined;
    this.form.ConsumptionMaterialWashingAmount = undefined;

    // Re-apply the service station's default location code so a subsequent
    // "create" starts with the preselected value again.
    this.applyDefaultLocationCode();
  }

  cancel() {
    this.onCanceled?.();
  }
}
